"use client";

import { useCallback } from "react";
import { KebabMenuItem } from "@zapier/shared";
import {
  useAccountRestriction,
  useBulkRestoreAssets,
  useTrashEnabledAssetPrefixes,
  useUserAccountRole,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { useNotification } from "@zapier/utils";

import { isAllOfPrefixes } from "../bulkActionRegistry";
import {
  type RestoreDestination,
  useRestoreNotification,
} from "../useRestoreNotification";
import { useTrashDeleteModalContext } from "./TrashDeleteModalContext";

interface Input {
  /** Already-prefixed asset id, e.g. "zap:123" (see convertToAssetId). */
  assetId: string;
  folderId?: string;
  /** Row's display title, used to name the asset in the restore toast. */
  assetName?: string;
  /** Row's previous folder, used as the restore destination in the toast. */
  folder?: RestoreDestination;
  /** Asset owner id, used to tell whose personal folder a folderless restore lands in. */
  owner?: { id?: number };
}

/** Builds the "Restore" / "Delete forever" kebab menu for a single trash-view row. */
export function useTrashRowMenuItems({
  assetId,
  folderId,
  assetName,
  folder,
  owner,
}: Input): KebabMenuItem[] {
  const t = useTranslations("buttons");
  const { createNotification } = useNotification();
  const { notifyRestoreSuccess } = useRestoreNotification({ folderId });
  const trashEnabledPrefixes = useTrashEnabledAssetPrefixes();
  const isSupported = isAllOfPrefixes([assetId], trashEnabledPrefixes);
  const bulkRestoreAssets = useBulkRestoreAssets({ folderId });
  const { requestDeleteForever } = useTrashDeleteModalContext();

  // Mirrors the bulk toolbar's delete gate (useBulkSelectItems) so a row can't
  // permanently delete what the account's publish-approval policy blocks in bulk.
  // Fails closed while the restriction is still loading, rather than assuming unrestricted.
  const { isPublishApprovalEnabled, isLoading: isAccountRestrictionLoading } =
    useAccountRestriction();
  const { isAdmin, isSuperAdmin, isAccountOwner } = useUserAccountRole();
  const isDeleteAllowed =
    isAdmin ||
    isSuperAdmin ||
    isAccountOwner ||
    (!isPublishApprovalEnabled && !isAccountRestrictionLoading);

  const handleRestore = useCallback(async () => {
    try {
      const { succeededAssetIds, failedAssetIds } =
        await bulkRestoreAssets.mutateAsync({
          assetIds: [assetId],
        });

      if (failedAssetIds.length === 0) {
        notifyRestoreSuccess({
          succeededAssetIds,
          single: { assetName, folder, owner },
        });
      } else {
        createNotification({ message: t("restore-error"), type: "error" });
      }
    } catch {
      createNotification({ message: t("restore-error"), type: "error" });
    }
  }, [
    assetId,
    assetName,
    folder,
    owner,
    bulkRestoreAssets,
    createNotification,
    notifyRestoreSuccess,
    t,
  ]);

  const unsupportedTooltipText = isSupported
    ? undefined
    : t("trash-action-unsupported");
  const deleteTooltipText =
    unsupportedTooltipText ??
    (isDeleteAllowed ? undefined : t("trash-delete-restricted"));

  return [
    {
      icon: "arrowRefresh",
      label: t("restore"),
      disabled: !isSupported,
      loading: bulkRestoreAssets.isPending,
      tooltipText: unsupportedTooltipText,
      onClick: () => void handleRestore(),
    },
    {
      icon: "actionTrash",
      label: t("permanently-delete"),
      hasWarning: true,
      disabled: !isSupported || !isDeleteAllowed,
      tooltipText: deleteTooltipText,
      onClick: () => requestDeleteForever(assetId),
    },
  ];
}
